/**
 * Oscilloscope Visualizer - Shows frequency spectrum
 */

import { BaseVisualizer, VisualizerConfig, VisualizerMetadata } from '../types';

export class OscilloscopeVisualizer extends BaseVisualizer {
  readonly metadata: VisualizerMetadata = {
    id: 'oscilloscope',
    name: 'Frequency Spectrum',
    description: 'Display frequency spectrum as vertical bars',
    category: '2d',
    defaultZoom: 1,
    supports3D: false,
    supportsAutoRotation: false,
    supportsPanning: true,
    supportsZooming: true,
  };

  render(config: VisualizerConfig): void {
    const { ctx, width, height, activeWaves, zoom, pan, mousePos, setHoveredFrequency, colorMode = 'wave' } = config;
    const cx = width / 2;
    const cy = height / 2;

    // Clear canvas
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);

    if (activeWaves.length === 0) return;

    // Calculate frequency range based on zoom level
    const maxFreq = Math.max(...activeWaves.map(w => w.freq), 1);
    const visibleFreqRange = (maxFreq * 1.2) / zoom;
    const totalFreqRange = maxFreq * 2;
    const maxPanOffset = Math.max(0, totalFreqRange - visibleFreqRange);
    const freqPanOffset = Math.max(0, Math.min((pan.x / width) * visibleFreqRange, maxPanOffset));
    const minVisibleFreq = freqPanOffset;
    const adjustedMaxVisibleFreq = freqPanOffset + visibleFreqRange;
    const freqRange = adjustedMaxVisibleFreq - minVisibleFreq;

    // Draw background grid
    this.drawBackgroundGrid(ctx, width, height, minVisibleFreq, adjustedMaxVisibleFreq, cy);

    // Separate waves by channel
    const pureLeftWaves = activeWaves.filter(w => w.channel === 'left');
    const pureRightWaves = activeWaves.filter(w => w.channel === 'right');
    const bothWaves = activeWaves.filter(w => (w.channel || 'both') === 'both' && !((w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0));
    const autoPanWaves = activeWaves.filter(w => (w.autoPanRate || 0) > 0 && (w.autoPanDepth || 0) > 0);
    const hasDistinctChannels = pureLeftWaves.length > 0 || pureRightWaves.length > 0;

    // Reset hovered frequency
    if (setHoveredFrequency) setHoveredFrequency(null);

    // Draw spectrums based on colorMode
    if (colorMode === 'channel' && hasDistinctChannels) {
      this.drawSpectrum(ctx, pureLeftWaves, '#3b82f6', 'Left', minVisibleFreq, freqRange, width, cy, mousePos, setHoveredFrequency);
      this.drawSpectrum(ctx, pureRightWaves, '#ec4899', 'Right', minVisibleFreq, freqRange, width, cy, mousePos, setHoveredFrequency);
      this.drawSpectrum(ctx, bothWaves, '#10B981', 'Center', minVisibleFreq, freqRange, width, cy, mousePos, setHoveredFrequency);
    } else {
      // Use wave colors in 'wave' mode
      this.drawSpectrumWithWaveColors(ctx, activeWaves, minVisibleFreq, freqRange, width, cy, mousePos, setHoveredFrequency);
    }

    // Draw auto-pan waves if present
    if (autoPanWaves.length > 0) {
      const panColor = this.getPanColor(0); // Simplified for now
      this.drawSpectrum(ctx, autoPanWaves, panColor, 'Auto-Pan', minVisibleFreq, freqRange, width, cy, mousePos, setHoveredFrequency);
    }

    // Draw frequency labels
    this.drawFrequencyLabels(ctx, width, height, minVisibleFreq, adjustedMaxVisibleFreq, freqRange);
  }

  private drawBackgroundGrid(ctx: CanvasRenderingContext2D, width: number, height: number, minFreq: number, maxFreq: number, cy: number): void {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 1;

    // Horizontal grid lines
    for (let i = 0; i <= 10; i++) {
      const y = (height / 10) * i;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }

    // Vertical grid lines
    const freqRange = maxFreq - minFreq;
    let freqInterval = 10;
    if (freqRange > 1000) freqInterval = 100;
    else if (freqRange > 500) freqInterval = 50;
    else if (freqRange > 200) freqInterval = 25;
    else if (freqRange > 100) freqInterval = 10;
    else if (freqRange > 50) freqInterval = 5;
    else freqInterval = 1;

    const startFreq = Math.ceil(minFreq / freqInterval) * freqInterval;
    for (let freq = startFreq; freq <= maxFreq; freq += freqInterval) {
      const freqNormalized = (freq - minFreq) / freqRange;
      const x = freqNormalized * width;
      if (x >= 0 && x <= width) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
    }

    // Center axis
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
    ctx.beginPath();
    ctx.moveTo(0, cy);
    ctx.lineTo(width, cy);
    ctx.stroke();
  }

  private drawSpectrum(
    ctx: CanvasRenderingContext2D,
    waves: any[],
    color: string,
    channelName: string,
    minFreq: number,
    freqRange: number,
    width: number,
    cy: number,
    mousePos: { x: number; y: number } | null | undefined,
    setHoveredFrequency: ((freq: { freq: number; amp: number; channel: string; color: string } | null) => void) | undefined
  ): void {
    if (waves.length === 0) return;

    // Group waves by frequency
    const frequencyBins = new Map<number, number>();
    waves.forEach(wave => {
      const currentAmp = frequencyBins.get(wave.freq) || 0;
      frequencyBins.set(wave.freq, currentAmp + wave.amp);
    });

    ctx.shadowBlur = 8;
    ctx.shadowColor = color;

    frequencyBins.forEach((totalAmp, freq) => {
      const freqNormalized = (freq - minFreq) / freqRange;
      const x = freqNormalized * width;

      if (x >= -10 && x <= width + 10) {
        const barHeight = (totalAmp / 100) * (cy - 20);

        // Check hover
        if (mousePos && setHoveredFrequency && Math.abs(mousePos.x - x) < 8 && mousePos.y >= cy - barHeight && mousePos.y <= cy) {
          const firstWave = waves.find(w => w.freq === freq);
          const displayFreq = firstWave?.originalFreq ?? freq;
          setHoveredFrequency({ freq: displayFreq, amp: totalAmp, channel: channelName, color });
        }

        // Draw bar
        ctx.fillStyle = color;
        ctx.fillRect(x - 4, cy - barHeight, 8, barHeight);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.strokeRect(x - 4, cy - barHeight, 8, barHeight);
      }
    });

    ctx.shadowBlur = 0;
  }

  private drawSpectrumWithWaveColors(
    ctx: CanvasRenderingContext2D,
    waves: any[],
    minFreq: number,
    freqRange: number,
    width: number,
    cy: number,
    mousePos: { x: number; y: number } | null | undefined,
    setHoveredFrequency: ((freq: { freq: number; amp: number; channel: string; color: string } | null) => void) | undefined
  ): void {
    if (waves.length === 0) return;

    // Group waves by frequency
    const frequencyBins = new Map<number, Array<any>>();
    waves.forEach(wave => {
      if (!frequencyBins.has(wave.freq)) {
        frequencyBins.set(wave.freq, []);
      }
      frequencyBins.get(wave.freq)!.push(wave);
    });

    frequencyBins.forEach((wavesList, freq) => {
      const freqNormalized = (freq - minFreq) / freqRange;
      const x = freqNormalized * width;

      if (x >= -10 && x <= width + 10) {
        const totalAmp = wavesList.reduce((sum, w) => sum + w.amp, 0);
        const barHeight = (totalAmp / 100) * (cy - 20);

        // Use helper to get color from first wave
        const color = this.getWaveColor(wavesList[0], 'wave');

        // Check hover
        if (mousePos && setHoveredFrequency && Math.abs(mousePos.x - x) < 8 && mousePos.y >= cy - barHeight && mousePos.y <= cy) {
          const displayFreq = wavesList[0]?.originalFreq ?? freq;
          setHoveredFrequency({ freq: displayFreq, amp: totalAmp, channel: 'Wave', color });
        }

        // Draw bar
        ctx.shadowBlur = 8;
        ctx.shadowColor = color;
        ctx.fillStyle = color;
        ctx.fillRect(x - 4, cy - barHeight, 8, barHeight);
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.strokeRect(x - 4, cy - barHeight, 8, barHeight);
        ctx.shadowBlur = 0;
      }
    });
  }

  private drawFrequencyLabels(ctx: CanvasRenderingContext2D, width: number, height: number, minFreq: number, maxFreq: number, freqRange: number): void {
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.font = '10px sans-serif';

    let freqInterval = 10;
    if (freqRange > 1000) freqInterval = 100;
    else if (freqRange > 500) freqInterval = 50;
    else if (freqRange > 200) freqInterval = 25;
    else if (freqRange > 100) freqInterval = 10;
    else if (freqRange > 50) freqInterval = 5;
    else freqInterval = 1;

    const startFreq = Math.ceil(minFreq / freqInterval) * freqInterval;
    for (let freq = startFreq; freq <= maxFreq; freq += freqInterval) {
      const freqNormalized = (freq - minFreq) / freqRange;
      const x = freqNormalized * width;
      if (x >= 0 && x <= width) {
        ctx.fillText(`${Math.round(freq)}Hz`, x - 15, height - 5);
      }
    }
  }
}
